import PropTypes from "prop-types";
import React, { useCallback, useEffect, useRef, useState } from "react";
import AdditionalDetailsReadRows from "./AdditionalDetailsReadRows";
import {
  requiresAdditionalDetailsReload,
  saveFailureMessage,
} from "./additionalDetailsErrors";
import CustomFieldControl from "./CustomFieldControl";
import {
  editableValue,
  hasEditableChanges,
  sameFieldValue,
} from "./editableValue";
import { formatValue } from "./formatValue";
import { comparableValue as comparable } from "./types";

const empty = (value) =>
  value == null ||
  (typeof value === "string" && !value.trim()) ||
  (Array.isArray(value) && !value.length);

/** Mount with a key for the authorized contact/snapshot; never rebase a draft implicitly. */
const AdditionalDetailsEditor = ({
  definitions,
  values,
  onSave,
  onCancel,
  onReload,
  onBusyChange,
  initialDraft,
  onDraftChange,
  recovery,
}) => {
  const [original] = useState(() =>
    Object.fromEntries(values.map((row) => [row.definition_id, row.value])),
  );
  const [draft, setDraft] = useState(() =>
    Object.fromEntries(
      definitions.map((field) => [
        field.id,
        field.editable
          ? editableValue(field, (initialDraft || original)[field.id])
          : original[field.id],
      ]),
    ),
  );
  const [errors, setErrors] = useState({});
  const [failure, setFailure] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const inFlight = useRef(false);
  const form = useRef(null);
  const editable = definitions.filter((field) => field.editable);
  const changes = editable
    .filter(
      (field) => comparable(draft[field.id]) !== comparable(original[field.id]),
    )
    .map((field) => ({
      definition_id: field.id,
      value: draft[field.id] ?? null,
    }));
  const recoveryRequired = requiresAdditionalDetailsReload(failure);
  const hasUserEdits = hasEditableChanges(definitions, original, draft);

  useEffect(() => {
    onDraftChange?.(draft);
  }, [draft, onDraftChange]);

  useEffect(() => {
    if (saving) {
      return;
    }
    const first = definitions.find(
      (field) => field.editable && errors[field.id],
    );
    if (first) {
      form.current
        ?.querySelector(
          `[data-definition-id="${first.id}"] input, [data-definition-id="${first.id}"] select, [data-definition-id="${first.id}"] textarea`,
        )
        ?.focus();
    }
  }, [errors, saving, definitions]);

  const cancel = useCallback(() => {
    if (inFlight.current) {
      return;
    }
    if (hasUserEdits) {
      setConfirmDiscard(true);
    } else {
      onCancel();
    }
  }, [hasUserEdits, onCancel]);

  useEffect(() => {
    const node = form.current;
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        if (event.target.getAttribute("aria-expanded") === "true") {
          return;
        }
        event.preventDefault();
        event.stopPropagation();
        cancel();
      }
    };
    node?.addEventListener("keydown", handleEscape);
    return () => node?.removeEventListener("keydown", handleEscape);
  }, [cancel]);

  const save = async (event) => {
    event.preventDefault();
    if (inFlight.current || recoveryRequired) {
      return;
    }
    const invalid = Object.fromEntries(
      editable
        .filter((field) => field.required && empty(draft[field.id]))
        .map((field) => [field.id, "This field is required."]),
    );
    setErrors(invalid);
    if (Object.keys(invalid).length) {
      const first = editable.find((field) => invalid[field.id]);
      form.current
        ?.querySelector(
          `[data-definition-id="${first.id}"] input, [data-definition-id="${first.id}"] select, [data-definition-id="${first.id}"] textarea`,
        )
        ?.focus();
      return;
    }
    inFlight.current = true;
    setSaving(true);
    onBusyChange?.(true);
    setFailure(null);
    try {
      await onSave(changes);
    } catch (error) {
      setFailure(error);
      if (error.errors && typeof error.errors === "object") {
        const allowed = Object.fromEntries(
          editable
            .filter((field) => typeof error.errors[field.id] === "string")
            .map((field) => [field.id, error.errors[field.id]]),
        );
        setErrors(allowed);
      }
    } finally {
      inFlight.current = false;
      setSaving(false);
      onBusyChange?.(false);
    }
  };

  const failureMessage = saveFailureMessage(failure);

  return (
    <form ref={form} onSubmit={save} style={{ padding: "0 1px" }}>
      {recovery && (
        <p role="status" className="alert alert-info py-2">
          {recovery.notice}
        </p>
      )}
      {failure && (
        <div role="alert" className="alert alert-danger">
          {failureMessage}
          {recoveryRequired && (
            <>
              {" "}
              <button
                type="button"
                className="border-0 bg-transparent p-0 align-baseline text-blue text-decoration-none text-nowrap"
                onClick={() => onReload?.(draft)}
              >
                Reload and Review
              </button>
            </>
          )}
        </div>
      )}
      {Object.keys(errors).length > 0 && (
        <p role="alert">
          {failure
            ? "Review the highlighted fields in Additional Details before saving."
            : "Complete the required field in Additional Details before saving."}
        </p>
      )}
      <AdditionalDetailsReadRows
        definitions={definitions.filter((field) => !field.editable)}
        values={values}
      />
      {editable.map((field) => (
        <div key={field.id} data-definition-id={field.id}>
          <CustomFieldControl
            field={field}
            menuPlacement="top"
            value={draft[field.id] ?? null}
            disabled={saving || recoveryRequired}
            error={errors[field.id]}
            onChange={(value) =>
              setDraft((current) => ({ ...current, [field.id]: value }))
            }
          />
          {recovery &&
            Object.hasOwn(recovery.conflicts, field.id) &&
            !sameFieldValue(
              field,
              draft[field.id],
              editableValue(field, recovery.conflicts[field.id]),
            ) && (
              <div
                role="note"
                aria-label={`Updated saved value for ${field.label}`}
                className="border-left border-warning pl-2 mb-3"
              >
                <details>
                  <summary>
                    Saved value changed. Your entry is kept above.
                  </summary>
                  <div
                    className="text-break"
                    style={{ whiteSpace: "pre-wrap" }}
                  >
                    {formatValue(field, recovery.conflicts[field.id])}
                  </div>
                </details>
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0"
                  disabled={saving || recoveryRequired}
                  onClick={() =>
                    setDraft((current) => ({
                      ...current,
                      [field.id]: editableValue(
                        field,
                        recovery.conflicts[field.id],
                      ),
                    }))
                  }
                >
                  Use saved value
                </button>
              </div>
            )}
        </div>
      ))}
      {confirmDiscard && (
        <div role="alert" className="alert alert-warning">
          Discard unsaved Additional Details changes?
          <button
            type="button"
            className="btn btn-secondary ml-2"
            disabled={saving}
            onClick={() => setConfirmDiscard(false)}
          >
            Keep Editing
          </button>
          <button
            type="button"
            className="btn btn-danger ml-2"
            disabled={saving}
            onClick={onCancel}
          >
            Discard Changes
          </button>
        </div>
      )}
      <div
        className="d-flex justify-content-end py-2"
        // Fixed select menus use a z-index of 1, including downward fallbacks.
        style={{ position: "sticky", bottom: 0, zIndex: 0 }}
      >
        <button
          type="button"
          className="btn btn-secondary mr-2"
          disabled={saving}
          onClick={cancel}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={saving || recoveryRequired || !editable.length}
        >
          {saving ? "Saving…" : "Save Changes"}
        </button>
      </div>
    </form>
  );
};

AdditionalDetailsEditor.propTypes = {
  definitions: PropTypes.array.isRequired,
  values: PropTypes.array.isRequired,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onReload: PropTypes.func,
  onBusyChange: PropTypes.func,
  initialDraft: PropTypes.object,
  onDraftChange: PropTypes.func,
  recovery: PropTypes.shape({
    notice: PropTypes.string.isRequired,
    conflicts: PropTypes.object.isRequired,
  }),
};
export default AdditionalDetailsEditor;
